import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
          <h1 className="text-4xl font-heading font-extrabold text-foreground mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-6">(Operator-Protective, B2B-First)</p>
          
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
            <p className="text-sm italic">Last updated: December 2024</p>
            
            <p>These Terms of Service ("Terms") govern the access to and use of the SaaS platform ("Service") operated by 2DS Finland Oy ("Company", "we", "us", "our").</p>
            <p className="font-semibold text-foreground">By creating an account, accessing, or using the Service, you ("User", "Customer", "Operator") agree to be bound by these Terms.</p>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Eligibility & Account Responsibility</h2>
              <p>1.1 The Service is intended exclusively for business users (B2B).</p>
              <p className="mt-2">1.2 By registering, you confirm that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>You act on behalf of a business entity, or</li>
                <li>You have legal authority to bind such entity.</li>
              </ul>
              <p className="mt-2">1.3 You are responsible for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Maintaining the confidentiality of login credentials</li>
                <li>All activity performed under your account</li>
                <li>Ensuring accuracy of data entered into the system</li>
              </ul>
              <p className="mt-2 italic">We are not responsible for unauthorized access resulting from your failure to secure credentials.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Description of the Service</h2>
              <p>The Service provides tools for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Tour package creation and management</li>
                <li>Booking and transaction tracking</li>
                <li>Payment status tracking</li>
                <li>Document storage</li>
                <li>Financial reporting dashboards</li>
              </ul>
              <p className="mt-4">We do not:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Act as a travel agency</li>
                <li>Process customer travel bookings on your behalf</li>
                <li>Handle payments between you and your customers or suppliers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">3. No Financial, Legal, or Accounting Advice</h2>
              <p>The Service provides information and tools only. We do not provide:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Accounting advice</li>
                <li>Tax advice</li>
                <li>Legal advice</li>
                <li>Financial guarantees</li>
              </ul>
              <p className="mt-2">All data, calculations, and reports are for informational purposes only.</p>
              <p className="font-semibold text-foreground mt-2">You remain solely responsible for compliance with local laws, taxes, accounting, and reporting obligations.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">4. User Content & Responsibility</h2>
              <p>4.1 You retain ownership of all data, documents, and content uploaded ("User Content").</p>
              <p className="mt-2">4.2 You confirm that:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>You have the legal right to upload such content</li>
                <li>The content does not violate laws, contracts, or third-party rights</li>
              </ul>
              <p className="mt-2">4.3 We are not liable for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Accuracy of User Content</li>
                <li>Legal consequences of stored documents</li>
                <li>Disputes between you and third parties</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">5. Subscriptions, Billing & Payments</h2>
              <p>5.1 Subscription plans are billed:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Monthly: €9.99/month</li>
                <li>Annually: €99.99/year</li>
              </ul>
              <p className="mt-2">5.2 Payments are processed via third-party payment providers (e.g., Stripe).</p>
              <p>5.3 We do not store credit card details.</p>
              <p className="mt-2">5.4 Failure to pay may result in:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Suspension of access</li>
                <li>Restricted functionality</li>
                <li>Eventual account termination</li>
              </ul>
              <p className="mt-2 font-semibold">No refunds are provided unless required by mandatory law.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">6. Suspension & Termination</h2>
              <p>We reserve the right to suspend or terminate accounts if:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>These Terms are violated</li>
                <li>Illegal or abusive activity is detected</li>
                <li>Non-payment persists</li>
              </ul>
              <p className="mt-2">You may terminate your subscription at any time.</p>
              <p className="font-semibold">Termination does not absolve outstanding payment obligations.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">7. Data Availability & Backup</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>We make reasonable efforts to maintain availability.</li>
                <li>The Service is provided "as is" and "as available".</li>
              </ul>
              <p className="mt-2">You are solely responsible for:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Exporting data</li>
                <li>Maintaining backups</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">8. Limitation of Liability</h2>
              <p>To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>We are not liable for indirect, incidental, or consequential damages.</li>
                <li>Total liability shall not exceed the fees paid in the last 12 months.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">9. Indemnification</h2>
              <p>You agree to indemnify and hold harmless 2DS Finland Oy from claims arising from:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Your use of the Service</li>
                <li>Your data</li>
                <li>Your violation of laws or third-party rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">10. Governing Law & Jurisdiction</h2>
              <p>These Terms are governed by the laws of Finland.</p>
              <p>Any disputes shall be resolved in the courts of Finland.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3">11. Changes to Terms</h2>
              <p>We may update these Terms. Continued use constitutes acceptance.</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
